import React from 'react'
import {
  fetchAndActivate,
  getBoolean,
  getNumber,
  getString,
  RemoteConfig,
} from 'firebase/remote-config'
import * as Sentry from '@sentry/react'
import {
  parseAudienceFlag,
  parseCompanyAudienceFlag,
} from './shared/audienceFlag'

export const FeatureFlagsContext = React.createContext<FeatureFlags | null>(
  null
)

function isIndexedDbClosingError(error: unknown): boolean {
  return (
    error instanceof DOMException &&
    error.name === 'InvalidStateError' &&
    error.message.includes('database connection is closing')
  )
}

/**
 * Firebase Installations errors that are expected operational noise:
 *
 *   1. "request-failed" with 403 PERMISSION_DENIED — fires when the
 *      Installations API is called without a Referer (bookmark, email
 *      link, bot/scanner). The API key's referrer restriction blocks it.
 *      Firebase's wording for the missing-Referer placeholder has shifted
 *      between "referer null" and "referer <empty>" over time, so we match
 *      on the error code + status instead of the placeholder string.
 *
 *   2. "not-registered" — fires when a previously-stored Firebase
 *      Installation ID is no longer recognised by the server (cleared site
 *      data, IndexedDB eviction, or a server-side rotation). Firebase
 *      auto-re-registers; no user impact.
 *
 *   3. "app-offline" — Firebase Installations checks `navigator.onLine`
 *      synchronously and throws this when the browser reports offline at
 *      boot. Triggers on slow Wi-Fi handshake, VPN reconnect, or brief
 *      network blip during cold start. The .catch swallows it; feature
 *      flags just default. CL-WEBAPP-GF.
 */
function isInstallationsNoiseError(error: unknown): boolean {
  const msg =
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as { message?: unknown }).message === 'string'
      ? (error as { message: string }).message
      : ''
  if (
    msg.includes('installations/request-failed') &&
    (msg.includes('403') || msg.includes('PERMISSION_DENIED'))
  ) {
    return true
  }
  if (msg.includes('installations/not-registered')) {
    return true
  }
  if (msg.includes('installations/app-offline')) {
    return true
  }
  return false
}

/**
 * Firebase Remote Config failures that are environmental noise:
 *
 *   - "fetch-timeout" — SDK couldn't reach Google within fetchTimeoutMillis
 *     (slow network, throttled tab, content blockers, mobile-network handoff).
 *   - "fetch-client-network" — same root cause, different symptom (also
 *     filtered globally in src/index.jsx beforeSend).
 *   - "storage-set" — Safari wipes IndexedDB out from under the SDK while a
 *     cache write is in flight (ITP eviction, storage quota, private browsing,
 *     or user-cleared site data). Browser reports "Database deleted by request
 *     of the user"; Firebase wraps it. CL-WEBAPP-HX.
 *
 * Feature flags load in the background; the page renders fine without them.
 * The .catch already swallows the error operationally — we just don't want
 * to flood Sentry with these.
 */
function isRemoteConfigNoiseError(error: unknown): boolean {
  const msg =
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as { message?: unknown }).message === 'string'
      ? (error as { message: string }).message
      : ''
  return (
    msg.includes('remoteconfig/fetch-timeout') ||
    msg.includes('remoteconfig/fetch-client-network') ||
    msg.includes('remoteconfig/storage-set')
  )
}

type FeatureFlagOverrideType = 'boolean' | 'string' | 'number'

type FeatureFlagOverrideValue = boolean | string | number

function areFeatureFlagQueryOverridesEnabled(): boolean {
  return import.meta.env.MODE !== 'production'
}

// Flag keys are lowercase, but operators paste override URLs by hand and some
// environments (browser address-bar autocomplete, copy/paste) mangle the key's
// case. Match the key case-insensitively so `?CHATWOOT_X=true` works the same as
// `?chatwoot_x=true`. An exact match wins; otherwise fall back to the first
// case-insensitive match.
function getSearchParamCaseInsensitive(
  params: URLSearchParams,
  key: string
): string | null {
  const exact = params.get(key)
  if (exact !== null) {
    return exact
  }
  const lowerKey = key.toLowerCase()
  for (const [paramKey, paramValue] of params) {
    if (paramKey.toLowerCase() === lowerKey) {
      return paramValue
    }
  }
  return null
}

function getFeatureFlagQueryOverride(
  key: string,
  type: FeatureFlagOverrideType
): FeatureFlagOverrideValue | null {
  if (!areFeatureFlagQueryOverridesEnabled() || typeof window === 'undefined') {
    return null
  }

  const value = getSearchParamCaseInsensitive(
    new URLSearchParams(window.location.search),
    key
  )

  if (value === null) {
    return null
  }

  switch (type) {
    case 'boolean':
      if (value === 'true') return true
      if (value === 'false') return false
      return null
    case 'string':
      return value
    case 'number': {
      if (value.trim() === '') return null
      const numberValue = Number(value)
      return Number.isFinite(numberValue) ? numberValue : null
    }
  }
}

function getBooleanFeatureFlagQueryOverride(key: string): boolean | null {
  const override = getFeatureFlagQueryOverride(key, 'boolean')
  return typeof override === 'boolean' ? override : null
}

function getStringFeatureFlagQueryOverride(key: string): string | null {
  const override = getFeatureFlagQueryOverride(key, 'string')
  return typeof override === 'string' ? override : null
}

function getNumberFeatureFlagQueryOverride(key: string): number | null {
  const override = getFeatureFlagQueryOverride(key, 'number')
  return typeof override === 'number' ? override : null
}

// The audience-gate semantics live in shared/ so the backend evaluates the same
// shape identically. Re-exported because callers import them from this module.
export { parseAudienceFlag, parseCompanyAudienceFlag }

/**
 * Resolve the identity an audience flag should evaluate against.
 *
 * While impersonating, the Firebase currentUser is the impersonated user, so
 * its uid is the wrong identity for a per-user allowlist. The impersonator's
 * original uid is carried in the `impersonatorUid` claim. Like
 * `useIsAutomationAdmin`, only the impersonator's identity counts, never the
 * impersonated user's.
 */
export function resolveAudienceUid(
  claims: {
    impersonating?: unknown
    impersonatorUid?: unknown
    [key: string]: unknown
  },
  currentUid: string | null
): string | null {
  if (
    claims.impersonating === true &&
    typeof claims.impersonatorUid === 'string'
  ) {
    return claims.impersonatorUid
  }
  return currentUid
}

export class FeatureFlags {
  remoteConfig: RemoteConfig | null
  ready: Promise<void>
  // True once Remote Config has fetched/activated (or immediately when there is
  // no Remote Config, e.g. local dev). Lets routes hold a loading state until
  // the flag value is resolved instead of redirecting on a stale default.
  private resolved = false
  // True only once a fetch actually activated. `resolved` also goes true on a
  // failed fetch, which would hand a default-on flag straight back to the
  // in-repo default; this does not. See `getResolvedBoolean`.
  private activated = false

  constructor(remoteConfig: RemoteConfig | null) {
    this.remoteConfig = remoteConfig
    this.ready = Promise.resolve()

    if (!remoteConfig) {
      return
    }

    this.ready = fetchAndActivate(remoteConfig)
      .then(() => {
        this.activated = true
      })
      .catch((e) => {
        if (isIndexedDbClosingError(e)) return
        if (isInstallationsNoiseError(e)) return
        if (isRemoteConfigNoiseError(e)) return
        Sentry.captureException(e)
      })
      .finally(() => {
        this.resolved = true
      })
  }

  // Whether flag values are ready to read. With no Remote Config (local dev)
  // values resolve to local defaults immediately, so this is always true there.
  isReady = (): boolean => this.remoteConfig === null || this.resolved

  // Whether a published Remote Config value is actually in effect.
  isActivated = (): boolean => this.remoteConfig === null || this.activated

  getBoolean = (key: string): boolean => {
    const override = getBooleanFeatureFlagQueryOverride(key)
    if (override !== null) {
      return override
    }

    if (!this.remoteConfig) {
      return true // Local development always has all features enabled
    }
    const result = getBoolean(this.remoteConfig, key)
    return result ?? false
  }

  /**
   * Boolean flag read that treats "no published value in effect" as off.
   *
   * `getBoolean` falls back to `REMOTE_CONFIG_DEFAULTS` until a fetch activates,
   * so a flag defaulting to `true` (see `DEFAULT_ON_FLAGS`) reads as on during
   * exactly the window a published `false` exists to cover, and for the whole
   * page load whenever the fetch fails. Gating on activation keeps the published
   * value authoritative. Query overrides still win, so manual testing is
   * unaffected.
   */
  getResolvedBoolean = (key: string): boolean => {
    const override = getBooleanFeatureFlagQueryOverride(key)
    if (override !== null) {
      return override
    }

    if (!this.isActivated()) {
      return false
    }

    return this.getBoolean(key)
  }

  getString = (key: string): string => {
    const override = getStringFeatureFlagQueryOverride(key)
    if (override !== null) {
      return override
    }

    if (!this.remoteConfig) {
      return ''
    }

    const result = getString(this.remoteConfig, key)
    return result ?? ''
  }

  getNumber = (key: string): number => {
    const override = getNumberFeatureFlagQueryOverride(key)
    if (override !== null) {
      return override
    }

    if (!this.remoteConfig) {
      return 0
    }

    const result = getNumber(this.remoteConfig, key)
    return result ?? 0
  }

  get = (key: string): boolean => this.getBoolean(key)

  /**
   * Read a reusable audience-gated flag for the current Firebase UID. Supports a
   * global `enabled` boolean plus an optional UID `allowlist` (see
   * `parseAudienceFlag`). Reusable by any future UID-allowlist rollout.
   */
  getAudienceFlag = (key: string, uid: string | null): boolean => {
    const override = getBooleanFeatureFlagQueryOverride(key)
    if (override !== null) {
      return override
    }

    // Local development always has all features enabled, mirroring getBoolean.
    if (!this.remoteConfig) {
      return true
    }

    return parseAudienceFlag(getString(this.remoteConfig, key), uid)
  }

  /**
   * Read a company-audience-gated flag: enabled globally, or when ANY of the
   * caller's `companyIds` is in the allowlist (see `parseCompanyAudienceFlag`).
   * Pass `[]` to evaluate the global `enabled` switch alone (e.g. the boot-time
   * Intercom gate, which has no company context yet).
   */
  getCompanyAudienceFlag = (
    key: string,
    companyIds: readonly string[]
  ): boolean => {
    const override = getBooleanFeatureFlagQueryOverride(key)
    if (override !== null) {
      return override
    }

    // Local development always has all features enabled, mirroring getBoolean.
    if (!this.remoteConfig) {
      return true
    }

    return parseCompanyAudienceFlag(
      getString(this.remoteConfig, key),
      companyIds
    )
  }
}
